
import React, { useEffect, useState } from "react";
import "../../css/BangGiaFlycam.css";

const JSON_URL =
    "https://raw.githubusercontent.com/VuongMinh01/MinMedia-anh/main/BangGia/BangGiaFlycam.json";

function formatPrice(amount, currency = "VND") {
    return `${new Intl.NumberFormat("vi-VN").format(amount)} ${currency}`;
}

function getYouTubeEmbedUrl(url = "") {
    try {
        const parsed = new URL(url);
        let id = "";

        if (parsed.hostname.includes("youtu.be")) {
            id = parsed.pathname.slice(1);
        } else if (parsed.hostname.includes("youtube.com")) {
            id = parsed.searchParams.get("v") || "";

            if (parsed.pathname.startsWith("/embed/")) {
                id = parsed.pathname.split("/")[2];
            }
        }

        return id
            ? `https://www.youtube-nocookie.com/embed/${id}`
            : "";
    } catch {
        return "";
    }
}

export default function Flycam() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            try {
                setError("");

                const response = await fetch(JSON_URL, {
                    signal: controller.signal,
                    cache: "no-store"
                });

                if (!response.ok) {
                    throw new Error(`Không tải được JSON (HTTP ${response.status})`);
                }

                const text = await response.text();

                if (!text.trim()) {
                    throw new Error(
                        "File BangGiaFlycam.json đang trống. Hãy thêm JSON và Commit changes trên GitHub."
                    );
                }

                let result;

                try {
                    result = JSON.parse(text);
                } catch {
                    throw new Error("File GitHub không chứa JSON hợp lệ.");
                }

                if (
                    !result.hero ||
                    !Array.isArray(result.packages) ||
                    !result.price
                ) {
                    throw new Error(
                        "JSON cần có các trường hero, packages và price."
                    );
                }

                setData(result);
            } catch (err) {
                if (err.name !== "AbortError") {
                    setError(err.message || "Không thể kết nối GitHub.");
                }
            }
        }

        loadData();

        return () => controller.abort();
    }, [retry]);

    if (error) {
        return (
            <main className="flycam-page">
                <section className="flycam-error">
                    <span className="flycam-kicker">MINMEDIA / FLYCAM</span>
                    <h1>Chưa thể tải dữ liệu</h1>
                    <p>{error}</p>
                    <button
                        type="button"
                        className="flycam-button"
                        onClick={() => setRetry((value) => value + 1)}
                    >
                        Thử tải lại ↗
                    </button>
                </section>
            </main>
        );
    }

    if (!data) {
        return (
            <main className="flycam-page">
                <div className="flycam-loading">
                    <span className="flycam-loader" />
                    <p>Đang tải dữ liệu MinMedia...</p>
                </div>
            </main>
        );
    }

    const { hero, video, packages, price, guarantees = [], contact = {} } =
        data;

    const embedUrl = getYouTubeEmbedUrl(video?.url);
    const titleLines = (hero.title || "GÓC NHÌN KHÁC.").split("\n");

    const contactHref = contact.phone
        ? `tel:${contact.phone.replace(/[^\d+]/g, "")}`
        : "#flycam-contact";

    return (
        <main className="flycam-page">
            <section className="flycam-hero">
                <div className="flycam-container flycam-hero-grid">
                    <div className="flycam-hero-copy">
                        <span className="flycam-kicker">
                            <span className="flycam-status-dot" />
                            {hero.eyebrow || "MINMEDIA / AERIAL FILMMAKING"}
                        </span>

                        <h1>
                            {titleLines.map((line, index) => (
                                <React.Fragment key={`${line}-${index}`}>
                                    {index > 0 && <br />}
                                    <span className={index === 1 ? "flycam-highlight" : ""}>
                                        {line}
                                    </span>
                                </React.Fragment>
                            ))}
                        </h1>

                        <p className="flycam-lead">{hero.description}</p>

                        <div className="flycam-hero-actions">
                            <a href="#flycam-video" className="flycam-button">
                                {hero.buttonText || "Khám phá video"} <span>↗</span>
                            </a>

                            <a
                                href={contactHref}
                                className="flycam-button flycam-button-ghost"
                            >
                                {hero.secondaryButtonText || "Liên hệ ngay"}
                            </a>
                        </div>

                        <div className="flycam-meta">
                            <span>WEDDING</span>
                            <span>TEAM BUILDING</span>
                            <span>EVENT</span>
                        </div>
                    </div>

                    <div className="flycam-hero-image">
                        <div className="flycam-image-shade" />
                        <span className="flycam-image-tag">CAPTURE THE MOMENT</span>
                        <div className="flycam-image-bottom">
                            <span>MINMEDIA</span>
                            <span>AERIAL VIEW / 01</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="flycam-video-section" id="flycam-video">
                <div className="flycam-container">
                    <div className="flycam-section-heading">
                        <div>
                            <span className="flycam-kicker">01 / SHOWREEL</span>
                            <h2>Nhìn sự kiện theo cách khác.</h2>
                        </div>
                        <p>
                            Những góc quay từ trên cao mở ra một góc nhìn mới cho
                            mỗi khoảnh khắc.
                        </p>
                    </div>

                    <div className="flycam-video-frame">
                        {embedUrl ? (
                            <iframe
                                src={embedUrl}
                                title={video?.title || "MinMedia Flycam Showreel"}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                loading="lazy"
                            />
                        ) : (
                            <div className="flycam-video-placeholder">
                                Chưa có đường dẫn video YouTube hợp lệ.
                            </div>
                        )}
                    </div>

                    <p className="flycam-video-caption">
                        {video?.title || "MinMedia Flycam Showreel"}
                    </p>
                </div>
            </section>

            <section className="flycam-services">
                <div className="flycam-container">
                    <div className="flycam-section-heading">
                        <div>
                            <span className="flycam-kicker">02 / OUR SERVICES</span>
                            <h2>Khoảnh khắc xứng đáng được lưu giữ.</h2>
                        </div>
                        <p>
                            Giải pháp ghi hình trên cao cho những sự kiện đặc biệt
                            và hoạt động tập thể.
                        </p>
                    </div>

                    <div className="flycam-package-grid">
                        {packages.map((item, index) => (
                            <article className="flycam-package" key={item.id || item.name}>
                                <div className="flycam-package-top">
                                    <span>{item.number || `0${index + 1}`} / SERVICE</span>
                                    <span className="flycam-arrow">↗</span>
                                </div>

                                <h3>{item.name}</h3>
                                <p className="flycam-package-description">
                                    {item.description}
                                </p>

                                {Array.isArray(item.features) && (
                                    <ul className="flycam-feature-list">
                                        {item.features.map((feature) => (
                                            <li key={feature}>
                                                <span>✓</span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                <a className="flycam-package-link" href={contactHref}>
                                    Tư vấn dịch vụ <span>→</span>
                                </a>
                            </article>
                        ))}
                    </div>

                    <div className="flycam-price-panel">
                        <div>
                            <span className="flycam-kicker">03 / SERVICE PRICE</span>
                            <h3>{price.label || "Gói dịch vụ từ"}</h3>
                            <p>Liên hệ để trao đổi về nhu cầu và lịch quay.</p>
                        </div>

                        <div className="flycam-price">
                            <strong>{formatPrice(price.amount, price.currency)}</strong>
                            <span>MINMEDIA / FLYCAM</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="flycam-commitment">
                <div className="flycam-container">
                    <div className="flycam-section-heading">
                        <div>
                            <span className="flycam-kicker">04 / OUR COMMITMENT</span>
                            <h2>Chỉn chu trong từng khung hình.</h2>
                        </div>
                    </div>

                    <div className="flycam-guarantee-list">
                        {guarantees.map((item, index) => (
                            <div className="flycam-guarantee" key={item}>
                                <span>0{index + 1}</span>
                                <p>{item}</p>
                                <span>↗</span>
                            </div>
                        ))}
                    </div>

                    <div className="flycam-contact" id="flycam-contact">
                        <div>
                            <span className="flycam-kicker">LET'S WORK TOGETHER</span>
                            <h2>Bắt đầu lên kế hoạch quay của bạn.</h2>
                        </div>

                        <a className="flycam-button" href={contactHref}>
                            {contact.label || "Liên hệ MinMedia"} <span>↗</span>
                        </a>
                    </div>
                </div>
            </section>
        </main>
    );
}
